import React, { createContext, useContext, useState, useEffect } from 'react';

const STORAGE_KEY = 'ps-theme-override';

function getWindow(hour) {
    return (hour >= 7 && hour < 19) ? 'day' : 'night';
}

function getBaselineTheme(hour) {
    return getWindow(hour) === 'day' ? false : true; // false = light, true = dark
}

function resolveTheme() {
    const hour = new Date().getHours();
    const currentWindow = getWindow(hour);

    try {
        const saved = localStorage.getItem(STORAGE_KEY);
        if (saved) {
            const override = JSON.parse(saved);
            if (override.window === currentWindow) {
                return override.theme === 'dark';
            }
            // Override is from a different window — discard it
            localStorage.removeItem(STORAGE_KEY);
        }
    } catch {}

    return getBaselineTheme(hour);
}

export const ThemeContext = createContext({
    isDark: false,
    toggleTheme: () => {},
});

export function ThemeProvider({ children }) {
    const [isDark, setIsDark] = useState(resolveTheme);

    // Apply class to <html>
    useEffect(() => {
        const root = document.documentElement;
        if (isDark) root.classList.add('dark');
        else root.classList.remove('dark');
    }, [isDark]);

    // Re-evaluate every minute — flips at 07:00 and 19:00 transitions
    useEffect(() => {
        const interval = setInterval(() => {
            setIsDark(resolveTheme());
        }, 60 * 1000);
        return () => clearInterval(interval);
    }, []);

    const toggleTheme = () => {
        const hour = new Date().getHours();
        const currentWindow = getWindow(hour);
        const next = !isDark;

        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify({ theme: next ? 'dark' : 'light', window: currentWindow }));
        } catch {}

        setIsDark(next);
    };

    return (
        <ThemeContext.Provider value={{ isDark, toggleTheme }}>
            {children}
        </ThemeContext.Provider>
    );
}

export const useTheme = () => useContext(ThemeContext);
